import type { ProbKey } from './keys';

export const en: Record<ProbKey, string> = {
  'app.eyebrow': 'Math · Probability',
  'app.title': 'Probability',
  'panel.experiment': 'Experiment',
  'panel.params': 'Parameters',
  'hint.line1': 'Pick an experiment, drag the controls, watch the distribution shift.',
  'hint.line2': 'Educational content lives in the probabilities, not the text.',
  'experiment.sample-space-lab.label': 'Sample-space lab',
  'experiment.sample-space-lab.info': 'Explore sample spaces and compute probabilities',
  'experiment.conditional-bayes.label': 'Conditional & Bayes',
  'experiment.conditional-bayes.info': 'Explore conditional probability and Bayes theorem',
  'experiment.random-variables.label': 'Random variables',
  'experiment.named-distributions.label': 'Named distributions',
  'experiment.galton-board.label': 'Galton board',
  'experiment.sums-of-iid.label': 'Sums of iid',
  'experiment.normal-calculator.label': 'Normal calculator',
  'experiment.classic-problems.label': 'Classic problems',
  'submode.sample-space-lab.2d6-grid.label': 'Two dice (grid)',
  'submode.sample-space-lab.2d6-sum.label': 'Two dice (sum)',
  'submode.sample-space-lab.1d6.label': 'One die',
  'submode.sample-space-lab.coin-flips.label': 'Coin flips',
  'submode.sample-space-lab.card-suit.label': 'Card suit',
  'submode.sample-space-lab.spinner.label': 'Spinner',
  'submode.sample-space-lab.venn-2set.label': 'Venn (2 sets)',
  'submode.sample-space-lab.venn-3set.label': 'Venn (3 sets)',
  'submode.conditional-bayes.tree.label': 'Tree',
  'submode.conditional-bayes.flowchart.label': 'Flowchart',
  'param.submode.label': 'Sub-mode',
  'param.preset.label': 'Preset',
  'param.prior.label': 'Prior probability',
  'param.sensitivity.label': 'Sensitivity (true positive rate)',
  'param.false-positive-rate.label': 'False positive rate',
  'param.show-formula-trace.label': 'Show Bayes formula',
  'param.flips.label': 'Number of flips',
  'param.pascal-overlay.label': "Show Pascal's triangle",
  'preset.conditional-bayes.medical-test.label': 'Medical test',
  'preset.conditional-bayes.spam-filter.label': 'Spam filter',
  'preset.conditional-bayes.monty-hall-as-bayes.label': 'Monty Hall as Bayes',
  'preset.conditional-bayes.draw-without-replacement.label': 'Draw without replacement',
  'preset.conditional-bayes.coin-coin.label': 'Coin flip twice',
  'preset.conditional-bayes.custom-2level.label': 'Custom 2-level',
  'preset.conditional-bayes.custom-3level.label': 'Custom 3-level',
  'btn.clear-event': 'Clear event',
  'btn.show-all-events': 'Show all named events',
  'btn.union': 'Union (∪)',
  'btn.intersect': 'Intersect (∩)',
  'btn.complement': 'Complement (ᶜ)',
  'btn.reset-preset': 'Reset preset',
  'btn.step-animation': 'Step animation',
  'stat.event-probability': 'P(E)',
  'stat.event-cardinality': '|E|',
  'stat.universe-size': '|Ω|',
  'stat.prior': 'Prior',
  'stat.posterior': 'Posterior',
  'stat.likelihood': 'Likelihood',
  'formula.bayes.statement': 'Bayes theorem',
  'message.siblings-sum-to-one': 'Probabilities sum to 1',
  'experiment.random-variables.info': 'Explore probability distributions through sampling',
  'submode.random-variables.2d6-sum.label': 'Sum of two dice',
  'submode.random-variables.2d6-diff.label': 'Absolute difference of two dice',
  'submode.random-variables.2d6-max.label': 'Maximum of two dice',
  'submode.random-variables.coin-flips-count.label': 'Count of heads in 5 flips',
  'submode.random-variables.custom-payoff.label': 'Custom payoff',
  'param.show-variance-bars.label': 'Show variance bands (±σ)',
  'param.show-empirical-bars.label': 'Show empirical distribution',
  'btn.roll-one': 'Roll 1',
  'btn.roll-100': 'Roll 100',
  'btn.roll-1000': 'Roll 1000',
  'btn.auto-roll': 'Auto-roll',
  'btn.reset': 'Reset',
  'stat.expectation': 'E[X]',
  'stat.variance': 'Var[X]',
  'stat.stdev': 'σ[X]',
  'stat.empirical-expectation': 'E[X] (empirical)',
  'stat.sample-count': 'Samples',
  'experiment.named-distributions.info':
    'Explore binomial, geometric, Poisson, and hypergeometric distributions',
  'submode.named-distributions.binomial.label': 'Binomial',
  'submode.named-distributions.geometric.label': 'Geometric',
  'submode.named-distributions.poisson.label': 'Poisson',
  'submode.named-distributions.hypergeometric.label': 'Hypergeometric',
  'param.n.label': 'Number of trials (n)',
  'param.p.label': 'Probability (p)',
  'param.lambda.label': 'Rate (λ)',
  'param.N-population.label': 'Population size (N)',
  'param.K-successes.label': 'Number of successes (K)',
  'param.show-cdf.label': 'Show CDF',
  'param.show-moments.label': 'Show mean and ±σ',
  'param.show-pascal-row.label': "Show Pascal's triangle row",
  'param.compare-to.label': 'Compare to...',
  'experiment.galton-board.info':
    'Simulate beads falling through a Galton board to explore the binomial distribution',
  'param.rows.label': 'Number of rows',
  'param.show-binomial-theoretical.label': 'Show theoretical binomial',
  'param.show-normal-approx.label': 'Show normal approximation',
  'param.show-continuity-bins.label': 'Show continuity correction',
  'param.speed.label': 'Auto-drop speed',
  'btn.drop-one': 'Drop 1',
  'btn.drop-100': 'Drop 100',
  'btn.drop-1000': 'Drop 1000',
  'btn.auto-drop': 'Auto-drop',
  'experiment.sums-of-iid.info':
    'Explore the sum and mean of independent, identically distributed random variables',
  'submode.sums-of-iid.uniform.label': 'Uniform [-1,1]',
  'submode.sums-of-iid.exponential.label': 'Exponential (λ=1)',
  'submode.sums-of-iid.bimodal.label': 'Bimodal',
  'submode.sums-of-iid.discrete-die.label': 'Discrete die',
  'submode.sums-of-iid.custom-3-point.label': 'Custom 3-point',
  'param.base-dist.label': 'Base distribution',
  'param.somregel.label': 'Show somregel formulas',
  'param.standardize.label': 'Standardize',
  'param.sum-or-mean.label': 'Sum or mean',
  'formula.somregel.mu': 'μ_S = n·μ',
  'formula.somregel.sigma-squared': 'σ²_S = n·σ²',
  'formula.somregel.sigma': 'σ_S = σ·√n',
  'btn.draw-100': 'Draw 100',
  'btn.draw-1000': 'Draw 1000',
  'btn.draw-10000': 'Draw 10000',
  'btn.auto-draw': 'Auto-draw',
  'experiment.normal-calculator.info': 'Explore the normal distribution and continuity correction',
  'submode.normal-calculator.density.label': 'Density',
  'submode.normal-calculator.bin-approx.label': 'Binomial approximation',
  'param.region-type.label': 'Region type',
  'param.mu.label': 'Mean (μ)',
  'param.sigma.label': 'Standard deviation (σ)',
  'param.a-bound.label': 'Lower bound (a)',
  'param.b-bound.label': 'Upper bound (b)',
  'param.show-z-scores.label': 'Show Z-scores',
  'param.show-68-95-99.label': 'Show 68-95-99.7 bands',
  'param.show-continuity-correction.label': 'Show continuity correction',
  'region.between.label': 'Between (a ≤ X ≤ b)',
  'region.leq.label': 'Less than or equal (X ≤ a)',
  'region.geq.label': 'Greater than or equal (X ≥ a)',
  'region.tails.label': 'Tails (|X| ≥ a)',
  'experiment.classic-problems.info': 'Explore Monty Hall, birthday paradox, and Buffon needle',
  'submode.classic-problems.monty-hall.label': 'Monty Hall problem',
  'submode.classic-problems.birthday.label': 'Birthday paradox',
  'submode.classic-problems.buffon.label': 'Buffon needle',
  'param.strategy.label': 'Strategy',
  'param.class-size.label': 'Class size (n)',
  'param.needle-length.label': 'Needle length (ℓ)',
  'param.show-formula.label': 'Show formula',
  'strategy.switch.label': 'Switch',
  'strategy.stay.label': 'Stay',
  'strategy.random.label': 'Random',
  'btn.play-1': 'Play 1',
  'btn.play-100': 'Play 100',
  'btn.play-10000': 'Play 10000',
  'stat.win-rate.switch': 'Switch win rate',
  'stat.win-rate.stay': 'Stay win rate',
  'stat.empirical-prob': 'Empirical P',
  'stat.theoretical-prob': 'Theoretical P',
  'stat.pi-estimate': 'π estimate',
  'axis.outcome': 'Outcome',
  'axis.probability': 'Probability',
  'axis.probability-cumulative': 'Cumulative probability',
  'axis.value': 'Value',
  'axis.density': 'Density',
  'axis.count-bin': 'Bin',
  'axis.count': 'Count',
  'venn.set.A': 'A',
  'venn.set.B': 'B',
  'venn.set.C': 'C',
  'param.intersection.label': 'Intersection size',
  'spinner.legend.sector': 'Sector',
};
